import { useQuery, type ApolloError } from '@apollo/client'
import { useParams } from 'react-router-dom'
import {
  GET_ORDERS,
  type IGetOrders,
  type IGetOrdersVariables,
  type OrderApiInput,
} from '@commerce/core'

/** One page of history. Enough for the screen; paging is not built yet. */
const PAGE_SIZE = 20

export interface UseOrders {
  orders: OrderApiInput[]
  /** How many the customer has in total, which can exceed what is loaded. */
  total: number
  hasMore: boolean
  loading: boolean
  error: ApolloError | undefined
}

/**
 * The shopper's order history, filtered server-side.
 *
 * `status` is passed to the API rather than filtered here because only the
 * first page is loaded: filtering client-side would narrow one page and call
 * it the answer, so a shopper with 30 orders would see "no cancelled orders"
 * while holding several on page two.
 *
 * `cache-and-network` because an order placed in this session should show up
 * on the way back to this screen, while a cached page still paints instantly.
 */
export const useOrders = (status?: string): UseOrders => {
  const { slug = '', sessionId = '' } = useParams()

  const { data, loading, error } = useQuery<IGetOrders, IGetOrdersVariables>(GET_ORDERS, {
    variables: {
      storefrontName: slug,
      sessionUid: sessionId,
      pagination: { pageNumber: 1, pageSize: PAGE_SIZE },
      ...(status ? { filter: { status } } : {}),
    },
    fetchPolicy: 'cache-and-network',
  })

  const result = data?.getOrders
  return {
    orders: result?.orders ?? [],
    total: result?.pagination?.total ?? 0,
    hasMore: result?.pagination?.hasNextPage ?? false,
    loading,
    error,
  }
}
